import React from 'react';
import { useQuery } from '@apollo/client';
import {
  Stack,
  Chip,
  CircularProgress,
  Alert,
  Typography,
} from '@mui/material';
import { getHubspotOwnersQuery } from '../graphql';
import {
  HubspotOwnersQuery,
  HubspotOwnersQueryVariables,
  HubspotOwnerFieldsFragment,
} from '@pv/common/graphql/operations';

interface HubspotOwnersProps {
  organizationId: string;
}

export const HubspotOwners: React.FC<HubspotOwnersProps> = ({
  organizationId,
}) => {
  const { data, loading, error } = useQuery<
    HubspotOwnersQuery,
    HubspotOwnersQueryVariables
  >(getHubspotOwnersQuery, {
    variables: { organizationId },
    skip: !organizationId,
  });

  if (loading) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <CircularProgress size={20} />
        <Typography variant="body2">Loading HubSpot owners...</Typography>
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert variant="outlined" severity="warning" sx={{ mt: 1 }}>
        Failed to load HubSpot owners: {error?.message}
      </Alert>
    );
  }

  const owners = data?.hubspotOwners;

  if (owners?.error) {
    return (
      <Alert variant="outlined" severity="warning" sx={{ mt: 1 }}>
        {owners.error}
      </Alert>
    );
  }

  if (
    !owners?.dealOwner &&
    !owners?.companyOwner &&
    !owners?.onboardingOwner &&
    !owners?.accountManagementOwner
  ) {
    return null;
  }

  return (
    <Stack direction="row" spacing={2} flexWrap="wrap" gap={1}>
      {owners.dealOwner && (
        <OwnerInfo ownerType="Deal Owner" owner={owners.dealOwner} />
      )}
      {owners.companyOwner && (
        <OwnerInfo ownerType="Company Owner" owner={owners.companyOwner} />
      )}
      {owners.onboardingOwner && (
        <OwnerInfo
          ownerType="Onboarding Owner"
          owner={owners.onboardingOwner}
        />
      )}
      {owners.accountManagementOwner && (
        <OwnerInfo
          ownerType="Account Management Owner"
          owner={owners.accountManagementOwner}
        />
      )}
    </Stack>
  );
};
interface OwnerInfoProps {
  ownerType: string;
  owner: HubspotOwnerFieldsFragment;
}
const OwnerInfo: React.FC<OwnerInfoProps> = ({ ownerType, owner }) => {
  const ownerName = [owner.firstName, owner.lastName].filter(Boolean).join(' ');
  const ownerEmail = owner.admin?.email || owner.email;
  const ownerInfo = ownerEmail ? `${ownerName} <${ownerEmail}>` : ownerName;
  return (
    <Stack direction="column" spacing={0.5}>
      <Typography variant="caption" color="textSecondary">
        {ownerType}
      </Typography>
      <Chip label={ownerInfo} color="default" variant="filled" />
    </Stack>
  );
};
